import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import "./useAdminFeedback.css";

/**
 * Shows Sentry's "Report a problem" feedback button while an admin is using
 * the admin area, and removes it when they leave.
 *
 * The feedback integration is registered with autoInject: false (see
 * index.jsx), so no page gets the button unless it calls this hook -- in
 * particular kiosk and student pages never do. Staff only: kiosks are shared
 * machines, and a student-facing button would invite noise.
 *
 * While mounted, the admin is set as the Sentry user so the form is
 * prefilled and reports (and any errors) can be followed up with them. This
 * is staff identity only; students are never set as the Sentry user.
 *
 * No-op outside production builds, where Sentry isn't initialized.
 */
export default function useAdminFeedback(user, isAdmin) {
  useEffect(() => {
    if (!isAdmin || !user?.UID) return undefined;
    const feedback = Sentry.getFeedback();
    if (!feedback) return undefined;

    Sentry.setUser({
      id: String(user.UID),
      email: user.email || undefined,
      username: [user.firstName, user.lastName].filter(Boolean).join(" ") || undefined,
    });
    const widget = feedback.createWidget();

    return () => {
      widget.removeFromDom();
      Sentry.setUser(null);
    };
  }, [isAdmin, user?.UID, user?.email, user?.firstName, user?.lastName]);
}
